<template>
  <v-dialog
    v-model="opened"
    content-class="dtp-photos"
    max-width="400"
    persistent
  >
    <h3>{{ $t('dtp.photos') }}</h3>
    <v-btn class="close-btn" icon @click="opened = false">
      <v-icon size="27" color="#464646"> mdi-close-circle-outline </v-icon>
    </v-btn>

    <div class="dtp-photos__gallery">
      <div v-if="dataLoading" class="dtp-photos__gallery-loader">
        <v-progress-circular
          size="50"
          width="3"
          color="warning"
          indeterminate
        />
      </div>

      <template v-else>
        <div
          v-if="!hasAnyPhotos && !newPhotos.length"
          class="dtp-photos__no-photos"
        >
          <v-icon size="50">mdi-image-remove</v-icon>
          <b>{{ $t('auto.filesListEmpty') }}</b>

          <v-btn
            v-if="$can('car_accident_image_upload')"
            color="info"
            elevation="0"
            small
            :loading="firstPhotoLoading"
            @click="onAddPhotoClick"
          >
            {{ $t('common.button.add') }}
          </v-btn>

          <image-uploader-new
            style="display:none"
            ref="image-uploader"
            :photo.sync="newPhotos[newPhotos.length]"
          />
        </div>

        <div
          v-else
          v-for="(photos, date, idx) in photosData"
          class="dtp-photos__gallery-item"
          :key="date"
        >
          <div class="dtp-photos__gallery-item-title">
            <time :datetime="date">{{ date }}</time>
          </div>

          <div class="dtp-photos__gallery-item-list">
            <template v-if="idx === 0">
              <div
                v-if="$can('car_accident_image_upload')"
                class="dtp-photos__gallery-photo"
              >
                <image-uploader-new :photo.sync="newPhotos[newPhotos.length]" />
              </div>

              <div
                v-for="(photo, idx) in newPhotos"
                :key="idx"
                class="dtp-photos__gallery-photo"
              >
                <image-uploader-new
                  :photo.sync="newPhotos[idx]"
                  @update:photo="onPhotosUpdate"
                />
              </div>
            </template>

            <div
              v-for="(photo, idx) in photos"
              :key="photo.name"
              class="dtp-photos__gallery-photo"
            >
              <image-uploader-new
                :readonly="!$can('car_accident_image_delete')"
                :download="$can('car_accident_image_download')"
                :photo.sync="photos[idx]"
                @update:photo="
                  (photo) => {
                    if (!photo) hasDeleted = true;
                    onPhotosUpdate();
                  }
                "
              />
            </div>
          </div>
        </div>
      </template>
    </div>

    <v-btn
      v-if="$can('car_accident_image_upload')"
      class="mt-3"
      color="warning"
      elevation="0"
      block
      :disabled="!newPhotos.length && !hasDeleted"
      @click="onSave"
    >
      {{ $t('common.btns.save') }}
    </v-btn>
  </v-dialog>
</template>

<script>
import ImageUploaderNew from '@/components/shared/ImageUploader.vue';

export default {
  components: { ImageUploaderNew },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    itemId: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    newPhotos: [],
    photosData: {},
    firstPhotoLoading: false,
    dataLoading: false,
    hasDeleted: false,
  }),
  watch: {
    opened(val) {
      if (!val) {
        this.newPhotos = [];
        this.photosData = {};
        this.firstPhotoLoading = false;
        this.hasDeleted = false;
      } else {
        this.fetchPhotos();
      }
    },
    newPhotos() {
      this.firstPhotoLoading = false;
    },
  },
  computed: {
    opened: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    hasAnyPhotos() {
      return Object.values(this.photosData).some((list) => !!list.length);
    },
  },
  methods: {
    async fetchPhotos() {
      this.dataLoading = true;
      const data = await this.$store.dispatch('dtp/fetchItem', this.itemId);

      this.setPhotosData(data.photos || {});
      this.dataLoading = false;
    },
    setPhotosData(data) {
      const photosData = { ...data };
      const dates = Object.keys(data);

      const formatNum = (num) => (num < 10 ? `0${num}` : num);
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const day = today.getDate();

      const lastMonth = dates[0];
      const currentDay = `${year}-${formatNum(month)}-${formatNum(day)}`;

      if (lastMonth !== currentDay) {
        photosData[currentDay] = [];
      }

      this.photosData = Object.keys(photosData)
        .sort()
        .reverse()
        .reduce((obj, key) => {
          const res = { ...obj, [key]: photosData[key] };
          return res;
        }, {});
    },
    onPhotosUpdate() {
      this.photosData = Object.entries(this.photosData).reduce(
        (data, [date, photos]) => {
          data[date] = photos.filter((photo) => photo); // eslint-disable-line
          return data;
        },
        {},
      );
      this.newPhotos = this.newPhotos.filter((photo) => photo);
    },
    onAddPhotoClick() {
      const el = this.$refs['image-uploader'].$el;
      const input = el.querySelector('input');
      input.click();
      input.onchange = () => (this.firstPhotoLoading = true); // eslint-disable-line
    },
    onSave() {
      const previousPhotoNames = Object.values(this.photosData).reduce(
        (arr, photos) => {
          const names = photos.map(({ name }) => name);
          return [...arr, ...names];
        },
        [],
      );
      const photoNames = this.newPhotos.map(({ name }) => name);

      this.$emit('save', [...previousPhotoNames, ...photoNames]);
      this.opened = false;
    },
  },
};
</script>

<style lang="scss">
.dtp-photos {
  padding: 10px;
  background-color: #fff;

  &__gallery {
    align-items: flex-start;
    position: relative;
    padding: 10px 5px;
    margin-top: 15px;
    height: 360px;
    display: flex;
    flex-wrap: wrap;
    border-radius: 5px;
    background-color: #eee;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: 10px;
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.15);
      background-clip: padding-box;
      border-radius: 20px;
      border: solid 2px rgba(0, 0, 0, 0);
    }
    &::-webkit-scrollbar-button {
      width: 0;
      height: 0;
      display: none;
    }
    &::-webkit-scrollbar-corner {
      background-color: transparent;
    }

    &-loader {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-item {
      width: 100%;
      padding-bottom: 10px;

      &-title {
        padding-left: 5px;
        padding-right: 5px;

        time {
          display: block;
          width: 100%;
          border-radius: 3px;
          text-align: center;
          font-size: 14px;
          font-weight: 500;
          color: #fff;
          background-color: #aaaaaa;
        }
      }

      &-list {
        display: flex;
        flex-wrap: wrap;
      }
    }

    &-photo {
      width: 50%;
      height: 150px;
      padding: 5px;
    }
  }

  &__no-photos {
    position: absolute;
    width: 100%;
    height: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #777;

    b {
      margin-top: 5px;
      margin-bottom: 10px;
      font-size: 18px;
    }
  }

  .img-upld .v-card {
    border: 2px dashed #aaa;
    font-size: 15px;
    box-shadow: none;
  }
}
</style>
